import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('mtu_token');
    const savedUser = localStorage.getItem('mtu_user');
    let cached = null;
    try { cached = savedUser ? JSON.parse(savedUser) : null; } catch { cached = null; }
    if (token && cached) {
      setUser(cached);
      authAPI.me().then(res => {
        // Security: if token resolves to a different user, force re-login
        if (cached.id && res.data.id && cached.id !== res.data.id) {
          console.warn('Session mismatch detected — cached user', cached.id, 'vs token user', res.data.id);
          logout();
          return;
        }
        setUser(res.data);
        localStorage.setItem('mtu_user', JSON.stringify(res.data));
      }).catch((err) => {
        // V71 — yalnız geçersiz oturumda (401/403) çıkış; 429/5xx/ağ hatasında önbellekteki kullanıcıyla devam
        const status = err?.response?.status;
        if (status === 401 || status === 403) logout();
      }).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Step 1: Password login → returns pending_token if MFA enabled
  // V71.3.1 — workType: 'office' | 'remote' (zorunlu; backend seçimsiz girişi 400 ile reddeder)
  const login = useCallback(async (username, password, rememberMe = false, workType) => {
    const res = await authAPI.login({ username, password, rememberMe, work_type: workType });

    if (res.data.mfa_required) {
      return { mfa_required: true, pending_token: res.data.pending_token };
    }

    if (res.data.email_otp_required) {
      return { email_otp_required: true, pending_token: res.data.pending_token, masked_email: res.data.masked_email };
    }

    if (res.data.mfa_setup_required) {
      return { mfa_setup_required: true, pending_token: res.data.pending_token };
    }

    // V71 — zorunlu şifre değişikliği: oturum açılmaz, Login ekranı şifre adımını gösterip sonra açar
    if (res.data.must_change_password && res.data.token) {
      return { must_change_password: true, token: res.data.token, user: res.data.user };
    }

    // No MFA — direct login
    const { token, user: userData } = res.data;
    localStorage.setItem('mtu_token', token);
    localStorage.setItem('mtu_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  }, []);

  // Step 2: Verify TOTP/backup code with pending_token
  const verifyMFA = useCallback(async (pendingToken, code) => {
    const res = await authAPI.verifyMFA({ pending_token: pendingToken, code });
    const { token, user: userData } = res.data;
    localStorage.setItem('mtu_token', token);
    localStorage.setItem('mtu_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  }, []);

  // Complete login after MFA setup (token comes from setup/verify response)
  const completeLogin = useCallback((token, userData) => {
    localStorage.setItem('mtu_token', token);
    localStorage.setItem('mtu_user', JSON.stringify(userData));
    setUser(userData);
  }, []);

  const logout = useCallback(() => {
    const token = localStorage.getItem('mtu_token');
    if (token) authAPI.cikis(token).catch(() => {});
    localStorage.removeItem('mtu_token');
    localStorage.removeItem('mtu_user');
    setUser(null);
  }, []);

  const isAdmin = user?.role === 'admin';

  /**
   * Modül ve işlem bazlı yetki kontrolü.
   * Admin her zaman true döner. Standard kullanıcılar permissions array'inden kontrol edilir.
   * @param {string} module - 'customers' | 'tickets' | 'marketing' | 'calendar'
   * @param {string} action - 'view' | 'create' | 'edit' | 'delete' | 'view_sensitive'
   */
  const refreshUser = useCallback(async () => {
    try {
      const res = await authAPI.me();
      setUser(res.data);
      localStorage.setItem('mtu_user', JSON.stringify(res.data));
    } catch {}
  }, []);

  // V70.12: useCallback ile sarmalandı — value identity stabilizesi için.
  // Önceki sürümde her render'da yeni function ref dönüyordu, consumer'ların
  // useEffect([hasPermission, ...]) deps'i sürekli "değişti" görünüp tüm
  // dashboard API'lerini ~150ms aralıkla yeniden çağırıyordu (saniyede 177 istek).
  const hasPermission = useCallback((module, action) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    const perms = user.permissions || [];
    const modulePerm = perms.find(p => p.module === module);
    if (!modulePerm) return false;
    return !!modulePerm[`can_${action}`];
  }, [user]);

  /** Shortcut: can this user see decrypted sensitive customer data? */
  const canViewSensitive = hasPermission('customers', 'view_sensitive');

  const value = useMemo(() => ({
    user, login, verifyMFA, completeLogin, logout, loading, isAdmin, hasPermission, canViewSensitive, refreshUser,
  }), [user, login, verifyMFA, completeLogin, logout, loading, isAdmin, hasPermission, canViewSensitive, refreshUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
